import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, AlertCircle, CheckCircle2, ChevronDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';
import useRevealObserver from '../hooks/useRevealObserver';
import AITestimonyBlock from '../components/AITestimonyBlock';
import { submitLead } from '../lib/api';

const Landing = () => {
  useRevealObserver();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const errorSummaryRef = useRef(null);

  const [form, setForm] = useState({
    fullName: '',
    company: '',
    phone: '',
    email: '',
    challenge: '',
    salesTeam: ''
  });

  const handle = (key) => (e) => {
    setForm({ ...form, [key]: e.target ? e.target.value : e });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'יש להזין שם מלא';
    if (!form.email.trim()) errs.email = 'יש להזין כתובת אימייל';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'כתובת אימייל לא תקינה';
    if (!form.phone.trim()) errs.phone = 'יש להזין מספר טלפון';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast({ title: 'שדות חובה חסרים', description: 'אנא בדקו את השדות המסומנים' });
      setTimeout(() => errorSummaryRef.current?.focus(), 50);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, source: 'landing-diagnostic' };
      ['company', 'challenge', 'salesTeam'].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await submitLead(payload);
      setSubmitted(true);
      toast({ title: 'תודה רבה', description: 'נחזור אליכם תוך זמן קצר.' });
    } catch (err) {
      toast({ title: 'שגיאה בשליחה', description: err?.response?.data?.detail || 'נסו שוב.' });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = (e) => {
    e?.preventDefault?.();
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => {
      document.getElementById('lp-fullName')?.focus();
    }, 600);
  };

  const renderField = ({ id, label, value, onChange, type = 'text', required = false, hint, autoComplete }) => {
    const hasError = !!errors[id];
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={`lp-${id}`} className="eyebrow text-navy/60">
          {label}{required && <span className="text-destructive mr-1" aria-hidden="true"> *</span>}
        </Label>
        {hint && <span className="text-navy/50 text-xs -mt-1">{hint}</span>}
        <Input
          id={`lp-${id}`}
          name={id}
          type={type}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          aria-required={required}
          aria-invalid={hasError}
          aria-describedby={hasError ? `lp-${id}-error` : undefined}
          className={`input-clean ${hasError ? 'border-b-destructive' : ''}`}
        />
        {hasError && (
          <p id={`lp-${id}-error`} className="text-destructive text-sm flex items-center gap-1.5 mt-1" role="alert">
            <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />{errors[id]}
          </p>
        )}
      </div>
    );
  };

  // === THANK YOU STATE ===
  if (submitted) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <header className="border-b border-navy/10 py-5 px-6 lg:px-12" role="banner">
          <Link to="/" className="font-serif text-xl text-navy">
            Gal<span className="text-bronze" aria-hidden="true">&</span>Blizovsky
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center px-6 py-20" role="main">
          <div className="max-w-2xl text-center">
            <div className="w-16 h-16 mx-auto bg-bronze/20 flex items-center justify-center mb-8" aria-hidden="true">
              <Check className="w-8 h-8 text-bronze" />
            </div>
            <h1 className="display-1 text-navy mb-6">תודה רבה.</h1>
            <p className="lede text-navy/70 text-lg mb-10">
              קיבלנו את הפנייה שלך. נציג בכיר מהצוות שלנו ייצור איתך קשר תוך 1-2 ימי עסקים
              לתיאום שיחת אבחון אסטרטגית למערך הצמיחה של העסק.
            </p>
            <Link
              to="/"
              className="btn-cta inline-flex items-center gap-3 bg-navy text-cream px-6 py-4 text-sm tracking-wide hover:bg-navy-700 transition-colors"
            >
              חזרה לעמוד הבית
              <ArrowLeft className="arrow w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" dir="rtl">
      {/* Minimal header */}
      <header className="border-b border-navy/10" role="banner">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl lg:text-2xl text-navy" aria-label="Gal&Blizovsky - חזרה לעמוד הבית">
            Gal<span className="text-bronze" aria-hidden="true">&</span>Blizovsky
          </Link>
          <button
            type="button"
            onClick={scrollToForm}
            className="hidden sm:inline-flex items-center gap-2 bg-navy text-cream px-5 py-3 text-sm tracking-wide hover:bg-bronze hover:text-navy transition-colors"
          >
            קבעו שיחת אבחון
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      <main role="main">
        {/* HERO */}
        <section className="hero-backdrop relative overflow-hidden py-16 lg:py-24" aria-labelledby="lp-hero-heading">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative">
            <p className="eyebrow text-bronze mb-6">למנכ״לים שעבדו קשה כדי להגיע לכאן</p>
            <h1 id="lp-hero-heading" className="display-1 text-navy text-balance mb-8 leading-[1.1]">
              אל בעל העסק שהגיע ל<span className="accent-underline">מיליון שקלים בשנה</span>,
              אבל מרגיש שכל שקל נוסף שהוא משקיע בדיגיטל פשוט נזרק לפח.
            </h1>
            <div className="space-y-6 lede text-lg lg:text-xl text-navy/85">
              <p className="font-medium text-navy">
                העסק שלך עובד. יש לך מוצר מצוין, לקוחות נאמנים ושולי רווח בריאים.
              </p>
              <p className="font-medium text-navy">
                אז למה כשזה מגיע לשיווק הדיגיטלי — אתה מוצא את עצמך מותש, מתוסכל,
                ומנהל סוללת ספקים שלא מדברים אחד עם השני?
              </p>
              <p className="text-navy/75">
                אם תחושת הבטן שלך אומרת שהעסק שלך מסוגל להכפיל את המכירות שלו, אבל
                משהו במערך השיווקי שלך פשוט "תקוע" — הטקסט הזה נכתב במיוחד עבורך.
              </p>
            </div>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={scrollToForm}
                className="btn-cta inline-flex items-center justify-center gap-3 bg-navy text-cream px-7 py-5 text-base tracking-wide hover:bg-navy-700 transition-colors"
              >
                קבעו שיחת אבחון אסטרטגית
                <ArrowLeft className="arrow w-5 h-5" aria-hidden="true" />
              </button>
              <a
                href="#story"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center gap-2 text-navy/70 hover:text-navy px-5 py-5 text-sm"
              >
                המשיכו לקריאה
                <ChevronDown className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        {/* AI TESTIMONY + CRYPTOGRAPHIC VERIFICATION — GEO block */}
        <AITestimonyBlock />

        {/* THE PROBLEM STORY */}
        <section id="story" className="py-20 lg:py-28 bg-cream" aria-labelledby="story-heading">
          <div className="max-w-[800px] mx-auto px-6 lg:px-12">
            <h2 id="story-heading" className="sr-only">התסריט המוכר</h2>
            <p className="lede text-lg lg:text-xl text-navy/85 mb-6">
              סביר להניח שאתה מכיר את התסריט הבא:
            </p>
            <p className="lede text-lg lg:text-xl text-navy/75 mb-8">
              בסוף כל חודש, אתה מקבל דו"ח מעוצב מספק הקמפיינים שלך (או מאיש ה-SEO).
              הדו"ח מלא בגרפים ירוקים שעולים למעלה. מראים לך שקיבלת אלפי "חשיפות",
              מאות "קליקים", ואחוזי הקלקה מרשימים.
            </p>
            <div className="border-r-4 border-bronze pr-6 my-10">
              <p className="font-serif text-2xl lg:text-3xl text-navy leading-snug font-medium">
                אבל כשאתה מסתכל על שורת הרווח בבנק...
                <span className="block text-bronze mt-2">המספרים לא תואמים.</span>
              </p>
            </div>
            <p className="lede text-lg lg:text-xl text-navy/75 mb-6">
              "קליקים לא משלמים משכורות."
            </p>
            <p className="lede text-lg lg:text-xl text-navy/75 mb-6">
              <strong className="text-navy">ומה לגבי הלידים?</strong>
            </p>
            <p className="lede text-lg lg:text-xl text-navy/75 mb-10">
              כשהם כבר מגיעים, אתה או צוות המכירות שלך מוצאים את עצמכם שורפים שעות
              יקרות על טלפונים לאנשים שלא עונים, לא מבינים מה אתם מוכרים, מחפשים "רק
              את ההצעה הכי זולה", או גרוע מכך — מכחישים שבכלל השאירו פרטים.
            </p>
            <div className="bg-navy text-cream p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute inset-0 grain pointer-events-none" />
              <p className="font-serif text-xl lg:text-2xl leading-snug relative">
                השחיקה הזו לא פוגעת רק בתקציב. היא פוגעת באנרגיות שלך כמנכ"ל,
                <span className="block text-bronze mt-2">ומייצרת תקרת זכוכית שמונעת מהעסק לצמוח.</span>
              </p>
            </div>
          </div>
        </section>

        {/* PROBLEM REFRAME */}
        <section className="py-20 lg:py-28 bg-cream-200" aria-labelledby="reframe-heading">
          <div className="max-w-[800px] mx-auto px-6 lg:px-12">
            <h2 id="reframe-heading" className="display-2 text-navy mb-8 leading-tight">
              הבעיה שלך היא לא "שיווק".<br />
              <span className="text-bronze">הבעיה שלך היא "ניהול מפוזר".</span>
            </h2>
            <div className="space-y-6 lede text-navy/80 text-lg lg:text-xl">
              <p>
                אתה מבין שחייבים נוכחות דיגיטלית חזקה כדי לגדול. אז שכרת קמפיינר
                לממומן, אולי איש לאורגני, בונה אתרים וכותב תוכן.
              </p>
              <p className="font-medium text-navy">
                אבל יצרת לעצמך בעיה חדשה: הפכת לפרויקטור של השיווק של עצמך.
              </p>
              <p>
                במקום לנהל את העסק, אתה מתווך בין מומחים שמדברים בשפות שונות.
                הפעולות נופלות בין הכיסאות, אין אסטרטגיה אחת שמחברת את הכל, והתקציב
                שלך מדמם על "ניסוי וטעייה" במקום לייצר מכונת צמיחה משומנת.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION INTRO */}
        <section className="dark-section py-20 lg:py-28 relative overflow-hidden" aria-labelledby="solution-heading">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative">
            <p className="eyebrow text-bronze mb-6">הפתרון</p>
            <h2 id="solution-heading" className="display-2 text-cream mb-10 leading-tight">
              תפסיק לאלתר. תתחיל לנהל צמיחה <span className="text-bronze">כסטנדרט הנדסי</span>.
            </h2>
            <div className="space-y-6 lede text-cream/85 text-lg lg:text-xl">
              <p>
                כאן בדיוק נכנסת לתמונה <strong className="text-bronze">Gal&Blizovsky</strong>.
              </p>
              <p className="text-cream/75">
                אנחנו לא "עוד סוכנות פרסום" שמוכרת לך לידים. אנחנו לא מציעים לך
                "קמפיין קסם".
              </p>
              <p className="font-medium text-cream">
                אנחנו מתפקדים כזרוע האסטרטגית והניהולית המלאה של העסק שלך
                <span className="text-bronze"> (Outsourced CMO)</span>.
              </p>
              <p className="text-cream/75">
                מגוף אחד אמין, איכותי ובעל סמכות, אתה מקבל מעטפת הנדסית שלמה שמתכללת
                את כל הפעילות הדיגיטלית שלך — תחת קורת גג אחת, אסטרטגיה אחת, ויעד אחד:
                <strong className="text-cream"> השורה התחתונה שלך.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* MECHANISM - 3 PILLARS */}
        <section className="py-20 lg:py-28 bg-cream" aria-labelledby="mechanism-heading">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <h2 id="mechanism-heading" className="display-2 text-navy mb-16 max-w-3xl">
              איך המנגנון שלנו עובד אחרת?
            </h2>
            <div className="space-y-px">
              {[
                {
                  num: '01',
                  title: 'סוף ל״מדדי הסרק״',
                  body: 'אותנו לא מעניינים קליקים זולים. אנחנו מודדים הצלחה במונחים של עלות רכישת לקוח (CAC), החזר השקעה (ROI), ומיקסום ההכנסות.'
                },
                {
                  num: '02',
                  title: 'פתרון משבר הלידים',
                  body: 'אנחנו לא רוצים להביא לך יותר לידים. אנחנו רוצים להביא לך פניות בשלות. אנחנו בונים "מסעות לקוח", דפי נחיתה מחממים ושאלוני סינון מחמירים. התוצאה? מי שמגיע לשיחת המכירה כבר מבין את הערך שלך ומוכן לסגירה. כל שיחת "מכירה קרה" הופכת לשיחת "ייעוץ חמה".'
                },
                {
                  num: '03',
                  title: 'תכלול של 100%',
                  body: 'קמפיינים, SEO, בוטים, דיוור, ניהול שותפים (Affiliate), תוכן ותשתיות המרה. הכל מנוהל על ידינו כגוף אחד, מסונכרן ועובד למען מטרה אחת.'
                }
              ].map((p) => (
                <article key={p.num} className="grid lg:grid-cols-12 gap-6 lg:gap-12 py-10 border-t border-navy/15 last:border-b">
                  <div className="lg:col-span-3">
                    <span className="font-serif text-5xl text-bronze" aria-hidden="true">{p.num}</span>
                  </div>
                  <div className="lg:col-span-9">
                    <h3 className="display-3 text-navy mb-4">{p.title}</h3>
                    <p className="lede text-navy/75 text-lg">{p.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* AUTHORITY */}
        <section className="py-20 lg:py-28 bg-cream-200" aria-labelledby="authority-heading">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <p className="eyebrow text-bronze mb-6">ההנהלה</p>
            <h2 id="authority-heading" className="display-2 text-navy mb-12 leading-tight max-w-3xl">
              שילוב נדיר בין מומחיות דיגיטלית קיצונית לסטנדרט הנדסי מחמיר.
            </h2>
            <p className="lede text-navy/75 text-lg mb-12 max-w-3xl">
              השיטה שלנו לא מבוססת על ניחושים. היא מבוססת על דאטה, תכנון מוקפד ובקרה.
              ההנהלה שמאחורי המערכת מביאה שילוב חסר תקדים לשוק השיווק הישראלי:
            </p>
            <div className="grid lg:grid-cols-2 gap-px bg-navy/10">
              <article className="bg-cream-200 p-8 lg:p-10">
                <p className="eyebrow text-bronze mb-3">מנכ״ל</p>
                <h3 className="display-3 text-navy mb-4">ציון גל</h3>
                <p className="lede text-navy/75 text-base lg:text-lg">
                  מהנדס מומחה ומי שהוביל אלפי פרויקטים מורכבים מול הגופים המחמירים
                  במשק (משרד ראש הממשלה, בנקים, רשויות). ציון מחדיר למערך השיווק שלך
                  סטנדרט של סדר, ניהול סיכונים ובקרה של פרויקט הנדסי: בודקים קלט,
                  מודדים פלט, ומבטיחים תוצאה.
                </p>
              </article>
              <article className="bg-cream-200 p-8 lg:p-10">
                <p className="eyebrow text-bronze mb-3">סמנכ״ל דיגיטל וצמיחה</p>
                <h3 className="display-3 text-navy mb-4">רז בליזובסקי</h3>
                <p className="lede text-navy/75 text-base lg:text-lg">
                  חי ונושם צמיחה דיגיטלית. הובלת מהלכי שיווק במאות מיליוני שקלים,
                  הקמת קהילות משווקים מובילות והכשרת אלפי מומחים. תחתיו, המערכות
                  הופכות למכונות תנועה חדות כתער.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* PROOF */}
        <section className="py-20 lg:py-28 bg-cream" aria-labelledby="proof-heading">
          <div className="max-w-[900px] mx-auto px-6 lg:px-12">
            <h2 id="proof-heading" className="display-2 text-navy mb-12 leading-tight">
              התוצאות שלנו<br />
              <span className="text-bronze">מדברות בשורת ההכנסות.</span>
            </h2>
            <ul className="space-y-6">
              {[
                { id: 'scale', content: <>מעורבות במהלכי שיווק ומכירה בהיקפים של <strong className="text-navy">מאות מיליוני שקלים</strong>.</> },
                { id: 'growth', content: <><strong className="text-navy">400% צמיחה</strong> בתוך 18 חודשים באחד ממקרי הבוחן המובילים שלנו.</> },
                { id: 'students', content: <>מעל <strong className="text-navy">1,000 אנשי מקצוע ומשווקים</strong> שלמדו מהשיטה שלנו.</> }
              ].map((item) => (
                <li key={item.id} className="flex items-start gap-4 lede text-lg lg:text-xl text-navy/80 border-b border-navy/10 pb-6">
                  <CheckCircle2 className="w-7 h-7 text-bronze flex-shrink-0 mt-1" aria-hidden="true" strokeWidth={1.8} />
                  <span>{item.content}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FORM CTA */}
        <section
          ref={formRef}
          id="form"
          className="py-20 lg:py-28 bg-cream-200 scroll-mt-12"
          aria-labelledby="form-heading"
        >
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
              <div className="lg:col-span-5">
                <p className="eyebrow text-bronze mb-6">הצעד הבא</p>
                <h2 id="form-heading" className="display-2 text-navy mb-8 leading-tight">
                  מוכן להפסיק להתפזר<br />
                  <span className="text-bronze">ולהתחיל באמת לצמוח?</span>
                </h2>
                <div className="space-y-5 lede text-navy/75 text-base lg:text-lg">
                  <p>
                    אם העסק שלך כבר עובד ומרוויח, אבל אתה מבין שפריצת תקרת הזכוכית
                    דורשת ניהול שיווקי מקצועי מא׳ ועד ת׳ — הצעד הבא שלך הוא לא לפתוח
                    עוד קמפיין בפייסבוק.
                  </p>
                  <p className="font-medium text-navy">
                    הצעד הבא שלך הוא שיחת אבחון.
                  </p>
                  <p>
                    זו אינה "שיחת מכירה". מטרת השיחה היא לנתח את מנועי הצמיחה שלך,
                    לאתר את צווארי הבקבוק שעוצרים אותך כרגע (איכות לידים? פיזור
                    ספקים? מסר לא מדויק?), ולבחון האם שיטת העבודה ההנדסית של
                    Gal&Blizovsky מתאימה לקחת אותך לשלב הבא.
                  </p>
                </div>
              </div>

              <div className="lg:col-span-7">
                <form
                  onSubmit={submit}
                  className="bg-cream p-8 lg:p-12 border border-navy/15 space-y-7"
                  noValidate
                  aria-label="טופס שיחת אבחון אסטרטגית"
                >
                  <div>
                    <p className="font-serif text-xl lg:text-2xl text-navy mb-2">
                      מלא את הפרטים למטה
                    </p>
                    <p className="text-navy/60 text-sm">
                      ובוא נבנה לעסק שלך מערכת שמייצרת ודאות תזרימית.
                    </p>
                  </div>

                  {Object.keys(errors).length > 0 && (
                    <div
                      ref={errorSummaryRef}
                      tabIndex={-1}
                      role="alert"
                      aria-live="assertive"
                      className="bg-destructive/10 border border-destructive/40 p-4 outline-none"
                    >
                      <p className="text-destructive font-medium mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" aria-hidden="true" />
                        יש לתקן {Object.keys(errors).length} שדות
                      </p>
                      <ul className="text-destructive/90 text-sm space-y-1 mr-6 list-disc">
                        {Object.entries(errors).map(([k, v]) => (
                          <li key={k}>{v}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-6">
                    {renderField({
                      id: 'fullName',
                      label: 'שם מלא',
                      value: form.fullName,
                      onChange: handle('fullName'),
                      required: true,
                      autoComplete: 'name'
                    })}
                    {renderField({
                      id: 'company',
                      label: 'שם העסק / חברה',
                      hint: 'כדי שנוכל לחקור קצת לפני השיחה',
                      value: form.company,
                      onChange: handle('company'),
                      autoComplete: 'organization'
                    })}
                    {renderField({
                      id: 'phone',
                      label: 'טלפון',
                      value: form.phone,
                      onChange: handle('phone'),
                      type: 'tel',
                      required: true,
                      autoComplete: 'tel'
                    })}
                    {renderField({
                      id: 'email',
                      label: 'דוא״ל',
                      value: form.email,
                      onChange: handle('email'),
                      type: 'email',
                      required: true,
                      autoComplete: 'email'
                    })}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="lp-challenge" className="eyebrow text-navy/60">
                      מה האתגר המרכזי שמייצר לך תסכול היום בדיגיטל?
                    </Label>
                    <Textarea
                      id="lp-challenge"
                      name="challenge"
                      value={form.challenge}
                      onChange={handle('challenge')}
                      rows={3}
                      className="bg-transparent border-0 border-b border-navy/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-navy resize-none"
                    />
                  </div>

                  <div className="flex flex-col gap-3" role="group" aria-labelledby="lp-salesTeam-label">
                    <span id="lp-salesTeam-label" className="eyebrow text-navy/60">
                      האם יש צוות מכירות פעיל?
                    </span>
                    <RadioGroup
                      value={form.salesTeam}
                      onValueChange={(v) => setForm({ ...form, salesTeam: v })}
                      className="flex gap-6"
                      aria-labelledby="lp-salesTeam-label"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="lp-salesTeam-y" />
                        <Label htmlFor="lp-salesTeam-y" className="text-navy text-sm cursor-pointer">כן</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="lp-salesTeam-n" />
                        <Label htmlFor="lp-salesTeam-n" className="text-navy text-sm cursor-pointer">לא</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    aria-busy={submitting}
                    className="btn-cta inline-flex items-center justify-center gap-3 bg-navy text-cream px-8 py-5 text-base tracking-wide hover:bg-bronze hover:text-navy transition-colors duration-300 w-full disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                        שולח...
                      </>
                    ) : (
                      <>
                        קבעו שיחת אבחון לבניית מערך הצמיחה שלכם
                        <ArrowLeft className="arrow w-5 h-5" aria-hidden="true" />
                      </>
                    )}
                  </button>

                  <p className="text-navy/55 text-xs leading-relaxed">
                    בשליחת הטופס אני מאשר/ת שקיבלתי את מדיניות הפרטיות, ומאשר/ת לשמור
                    את הנתונים לצורך יצירת קשר.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Closing tag line */}
        <section className="dark-section py-12 relative overflow-hidden" aria-label="חתימת המותג">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center relative">
            <p className="font-serif italic text-cream/80 text-lg lg:text-xl">
              Gal<span className="text-bronze" aria-hidden="true">&</span>Blizovsky —
              <span className="text-bronze"> הנדסת מנגנוני צמיחה לעסקים.</span>
            </p>
          </div>
        </section>
      </main>

      {/* Mini footer */}
      <footer className="bg-navy text-cream/60 py-6" role="contentinfo">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 flex flex-col sm:flex-row justify-between gap-3 text-xs">
          <p>© {new Date().getFullYear()} Gal&Blizovsky. כל הזכויות שמורות.</p>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-bronze">עמוד הבית</Link>
            <Link to="/about" className="hover:text-bronze">מי אנחנו</Link>
            <a href="mailto:office@gal-bl.co.il" className="hover:text-bronze">office@gal-bl.co.il</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
